import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import {
  CheckCircle2,
  ChevronRight,
  FileText,
  Flag,
  ScanSearch,
  TrendingUp,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys } from '~/api/queryKeys';
import { toAttributePayloadValue } from '~/components/common/attribute-editor/attribute-value';
import { CopyIconButton } from '~/components/common/copy-icon-button';
import { EnrichedAttributeRow } from '~/components/common/enriched-attribute-row';
import { EnrichmentScoreSection } from '~/components/common/enrichment-score-section';
import { ExternalLinkIconButton } from '~/components/common/external-link-icon-button';
import { InfoTooltip } from '~/components/common/info-tooltip/info-tooltip';
import { ProductImageGallery } from '~/components/common/product-image-gallery';
import { RefreshIconButton } from '~/components/common/refresh-icon-button';
import { ResizableSplit } from '~/components/common/resizable-split/resizable-split';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { SourceShippingSection } from '~/components/common/source-shipping-section';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { Skeleton } from '~/components/ui/skeleton';
import { Switch } from '~/components/ui/switch';
import { langfuseUserUrl } from '~/constants/config/env';
import { sortByImpact, sortImpacts } from '~/constants/impact';
import { AttributeFilterCard } from '~/features/catalog-operations/components/attribute-filter-card';
import type {
  AttributeCardView,
  AttributeExportScope,
} from '~/features/catalog-operations/components/attribute-filter-card';
import { CatalogOperationsLayout } from '~/features/catalog-operations/components/catalog-operations-layout';
import { GapAnalysis } from '~/features/catalog-operations/components/gap-analysis/gap-analysis';
import { GoogleAdsConnectBanner } from '~/features/catalog-operations/components/google-ads-connect-banner';
import { ProductCategory } from '~/features/catalog-operations/components/product-category';
import { ProductDeliveryHistoryLink } from '~/features/catalog-operations/components/product-delivery-history-link';
import { ProductPager } from '~/features/catalog-operations/components/product-pager';
import {
  ProductVersionTimeline,
  ViewingVersionBanner,
} from '~/features/catalog-operations/components/product-version-timeline';
import { SearchDescriptors } from '~/features/catalog-operations/components/search-descriptors';
import { SearchImpactTab } from '~/features/catalog-operations/components/search-impact/search-impact-tab';
import { LanguageCompareView } from '~/features/catalog-operations/components/translations/language-compare-view';
import {
  useBatchHeader,
  useProductDetail,
  useProductAttributeEdits,
  useProductEnrichment,
  useProductGapAnalysis,
  useRefreshProductGapAnalysis,
} from '~/features/catalog-operations/hooks/use-batch-detail';
import { useDescriptionDescriptorViews } from '~/features/catalog-operations/hooks/use-description-descriptor-views';
import { useProductOnDemandScore } from '~/features/catalog-operations/hooks/use-product-on-demand-score';
import {
  useProductSearchImpact,
  useRefreshProductSearchImpact,
} from '~/features/catalog-operations/hooks/use-product-search-impact';
import { useProductSiblings } from '~/features/catalog-operations/hooks/use-product-siblings';
import type { ProductSibling } from '~/features/catalog-operations/hooks/use-product-siblings';
import {
  tagsTrendsScope,
  useProductTagsTrends,
} from '~/features/catalog-operations/hooks/use-product-tags-trends';
import {
  useInternalProductVersions,
  useProductVersion,
  useProductVersions,
} from '~/features/catalog-operations/hooks/use-product-versions';
import { useProductTranslations } from '~/features/catalog-operations/hooks/use-translations';
import { useUpdateProductAttribute } from '~/features/catalog-operations/hooks/use-update-product-attribute';
import { applyCurrentValues } from '~/features/catalog-operations/mappers/catalog-operations.mapper';
import { useProductsListingStore } from '~/features/catalog-operations/store/products-listing.store';
import type {
  BatchDetailTab,
  ProductDetail,
  ProductTab,
} from '~/features/catalog-operations/types/catalog-operations.types';
import { indexAttributeEdits } from '~/features/catalog-operations/utils/attribute-edits';
import {
  isFeedAuditSource,
  isTrialSource,
} from '~/features/catalog-operations/utils/batch-flow-icons';
import type { MembershipReturnState } from '~/features/catalog-operations/utils/insight-membership';
import { toMetaVisibleOptimizations } from '~/features/catalog-operations/utils/meta-hidden-attributes';
import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { useFeatureGate } from '~/hooks/useFeatureGate';
import { cn } from '~/utils/cn';
import { formatDateTime, getTimeZoneAbbrev } from '~/utils/date';
import { triggerBrowserDownload } from '~/utils/download-file';
import { formatSourceShippingWireValue } from '~/utils/source-shipping';

const googleLogo = '/assets/images/google.svg';
const metaLogo = '/assets/images/meta.svg';
const PLATFORM_LOGO: Record<string, string> = { Google: googleLogo, Meta: metaLogo };
const sourceDestinationArrow = '/assets/images/source-destination-arrow.svg';

/** Max chips shown per field (Tags/Trends) before collapsing behind "+n more". */
const MAX_CHIPS_PER_FIELD = 4;

type ProductDetailTab =
  | 'product-detail'
  | 'gap-analysis'
  | 'search-descriptors'
  | 'search-impact'
  | 'languages'
  | 'version-history';

const DETAIL_TABS: { value: ProductDetailTab; label: string }[] = [
  { value: 'product-detail', label: 'Product Detail' },
  { value: 'gap-analysis', label: 'Gap Analysis' },
  { value: 'search-descriptors', label: 'Search Descriptors' },
  { value: 'languages', label: 'Languages' },
  { value: 'version-history', label: 'History' },
];

/** Replaces Gap Analysis + Search Descriptors when the `search_impact_ui` gate is on. */
const SEARCH_IMPACT_TAB: { value: ProductDetailTab; label: string } = {
  value: 'search-impact',
  label: 'Search Impact',
};

/** Bookkeeping columns on an archived version that describe the row's storage,
 *  not the product. Hidden so the field list reads as product data. */
const VERSION_FIELD_DENYLIST = new Set([
  'id',
  'feed_catalog_id',
  'product_id',
  'subsidiary_id',
  'content_hash',
  'created_at',
  'updated_at',
  'last_seen_at',
  'ingested_at',
  'deleted_at',
  'version',
  'version_valid_from',
  'last_curation_run_id',
]);

/**
 * Flattens one archived version's column map into renderable rows.
 *
 * The payload is `Record<string, unknown>` straight off the wire — some columns
 * are JSON (extra_attributes, custom_labels) and would crash React if rendered
 * directly, and nulls mean "not set", which is not the same as an empty string.
 * The GMC source shipping columns get the same human formatting as the live
 * Shipping & logistics section ("4.5 kg", not `{"value":4.5,"unit":"kg"}`).
 */
function toVersionFieldRows(
  fields: Record<string, unknown>,
  tCommon: TFunction<'common'>,
): { field: string; value: string }[] {
  const rows: { field: string; value: string }[] = [];
  for (const [field, raw] of Object.entries(fields)) {
    if (VERSION_FIELD_DENYLIST.has(field) || raw === null || raw === undefined) continue;
    const shippingValue = formatSourceShippingWireValue(field, raw, tCommon);
    const value =
      shippingValue ??
      (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean'
        ? String(raw)
        : JSON.stringify(raw));
    if (value === '' || value === '{}' || value === '[]') continue;
    rows.push({ field, value });
  }
  return rows.sort((a, b) => a.field.localeCompare(b.field));
}

const STATUS_CONFIG = {
  confirmed: {
    label: 'Confirmed',
    className: 'bg-success-50 text-success',
    icon: CheckCircle2,
  },
  'needs-attention': {
    label: 'Needs Attention',
    className: 'bg-warning-50 text-warning',
    icon: TrendingUp,
  },
  failed: { label: 'Failed', className: 'bg-brand-50 text-destructive', icon: TrendingUp },
};

interface Props {
  batchId: string;
  productId: string;
  /** When 'needs-attention', breadcrumb returns to the standalone Products Needing Attention list.
   *  When 'batch-detail', it skips the Products crumb and returns to the batch page. */
  from?: 'needs-attention' | 'batch-detail' | undefined;
  /** Where "Batch Details" lands when `from='batch-detail'` — the tab and the
   *  insight drawer the user opened this product from, so it re-opens on return. */
  batchReturn?: { tab?: BatchDetailTab; membership?: MembershipReturnState };
  /** Tab the user came from on the per-batch listing — propagated back through
   *  the breadcrumb so the listing re-opens on the same tab. */
  tab?: ProductTab | undefined;
  /** When true, the enrichment-score card and optimization accordion are
   *  hidden (and the enrichment query is skipped). Used by the global
   *  needs-attention detail view since those products haven't been enriched. */
  hideEnrichment?: boolean;
  /** Active detail tab — persisted in the URL so the tab survives a hard refresh. */
  detailTab?: ProductDetailTab;
  /**
   * Where this page is rendering. `page` (default) keeps the catalog-operations
   * shell and breadcrumb. `modal` drops both — the host dialog owns the frame,
   * its scrolling and its close affordance, and there is no trail to show
   * because the operator never left the page they opened it from.
   */
  chrome?: 'page' | 'modal';
}

/** Frame for the modal variant: the dialog supplies the surface and scroll, so
 *  this only guards the content against a zero-width flex parent. */
function ModalShell({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return (
    <div data-testid={testId} className="min-w-0">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview row helpers
// ---------------------------------------------------------------------------

/** Number of leading characters kept before a long identifier starts eliding. */
const ID_TAIL_CHARS = 18;

/**
 * Long identifier on one line. Retailer SKUs differ from each other in their
 * *tail*, so the head is what gets truncated — a trailing ellipsis would hide
 * the only part that identifies the record. Pure CSS (two flex children, the
 * first allowed to shrink), so it needs no measurement or resize listener.
 */
function TailSafeText({ value, className }: { value: string; className?: string }) {
  if (value.length <= ID_TAIL_CHARS + 8) {
    return (
      <span className={cn('truncate', className)} title={value}>
        {value}
      </span>
    );
  }
  return (
    <span className={cn('flex min-w-0 items-baseline', className)} title={value}>
      <span className="truncate">{value.slice(0, value.length - ID_TAIL_CHARS)}</span>
      <span className="flex-none">{value.slice(value.length - ID_TAIL_CHARS)}</span>
    </span>
  );
}

/**
 * Retailer category path. The leaf segment carries the meaning, so the leading
 * segments compress first and stay muted, keeping the specific category legible
 * however narrow the pane gets.
 */
function CategoryPath({ value }: { value: string }) {
  const parts = value
    .split('>')
    .map((part) => part.trim())
    .filter(Boolean);
  const leaf = parts.at(-1) ?? value;
  const head = parts.slice(0, -1).join(' › ');

  if (!head) {
    return (
      <span
        data-testid="product-detail-category-leaf"
        className="truncate text-sm font-semibold"
        title={value}
      >
        {leaf}
      </span>
    );
  }
  return (
    <span className="flex min-w-0 items-baseline gap-1" title={value}>
      <span className="text-muted-foreground truncate text-xs">{head}</span>
      <span className="text-muted-foreground flex-none text-xs">›</span>
      <span data-testid="product-detail-category-leaf" className="flex-none text-sm font-semibold">
        {leaf}
      </span>
    </span>
  );
}

/** One identity row — label beside value on a single line, so the panel spends
 *  its height on content rather than on stacked label/value pairs. */
function IdentityRow({ label, children }: { label: string; children: React.ReactNode }) {
  const testId = `product-detail-identity-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div
      data-testid={testId}
      className="grid grid-cols-[92px_minmax(0,1fr)] items-center gap-3 px-4 py-2"
    >
      <span className="text-muted-foreground text-xs">{label}</span>
      <div data-testid={`${testId}-value`} className="flex min-w-0 items-center gap-1.5">
        {children}
      </div>
    </div>
  );
}

/** One enrichment-output count. The bar makes relative magnitude readable
 *  without comparing digits; zero recedes so a scan lands on what exists. */
function SignalRow({
  label,
  count,
  max,
  isLoading,
  onSelect,
  testId: testIdProp,
}: {
  label: string;
  count: number;
  /** Largest count in the group — sets the bar's full width. */
  max: number;
  isLoading?: boolean | undefined;
  /** Reveals the data behind the count. Omitted when there is nowhere to go;
   *  a zero count is never actionable because there is nothing to show. */
  onSelect?: (() => void) | undefined;
  /** E2E `data-testid` root; defaults to one derived from `label`. */
  testId?: string;
}) {
  const isZero = count === 0;
  const isActionable = !!onSelect && !isZero && !isLoading;
  const testId = testIdProp ?? `product-detail-signal-${label.toLowerCase().replace(/\s+/g, '-')}`;

  const body = (
    <>
      <span className="text-muted-foreground min-w-0 flex-1 truncate text-left text-xs">
        {label}
      </span>
      <span className="bg-muted h-1 w-14 flex-none overflow-hidden rounded-full">
        <span
          className="bg-primary block h-full rounded-full"
          style={{ width: `${String(max > 0 ? Math.round((count / max) * 100) : 0)}%` }}
        />
      </span>
      {isLoading ? (
        <Skeleton data-testid={`${testId}-loading`} className="h-4 w-8 flex-none" />
      ) : (
        <span
          data-testid={`${testId}-count`}
          className={cn(
            'w-12 flex-none text-right text-sm tabular-nums',
            isZero ? 'text-muted-foreground font-medium' : 'font-semibold',
          )}
        >
          {count.toLocaleString()}
        </span>
      )}
    </>
  );

  if (!isActionable) {
    return (
      <div data-testid={testId} className="flex items-center gap-3 px-4 py-2">
        {body}
      </div>
    );
  }
  return (
    <Button
      data-testid={`${testId}-button`}
      type="button"
      variant="ghost"
      onClick={onSelect}
      title={`Show ${label.toLowerCase()}`}
      className="hover:bg-background flex h-auto w-full cursor-pointer items-center gap-3 rounded-none px-4 py-2"
    >
      {body}
    </Button>
  );
}

/** Compact chip field with a "+n more" roll-up — tags and trends are unbounded,
 *  and an empty one reads as a note rather than consuming a full row. */
function ChipField({
  items,
  expanded,
  onExpand,
  emptyLabel,
  className,
  testId,
}: {
  items: string[];
  expanded: boolean;
  onExpand: () => void;
  emptyLabel: string;
  className: string;
  /** Automation hook: list root, `-badge` per chip, `-more-button`, `-empty`. */
  testId: string;
}) {
  if (items.length === 0) {
    return (
      <span data-testid={`${testId}-empty`} className="text-muted-foreground text-xs">
        {emptyLabel}
      </span>
    );
  }
  const shown = expanded ? items : items.slice(0, MAX_CHIPS_PER_FIELD);
  return (
    <div data-testid={testId} className="flex min-w-0 flex-wrap items-center gap-1.5">
      {shown.map((item) => (
        <Badge
          key={item}
          data-testid={`${testId}-badge`}
          variant="outline"
          className={cn(
            'rounded-md px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap',
            className,
          )}
        >
          {item}
        </Badge>
      ))}
      {!expanded && items.length > MAX_CHIPS_PER_FIELD && (
        <Button
          data-testid={`${testId}-more-button`}
          type="button"
          variant="link"
          onClick={onExpand}
          className="h-auto cursor-pointer p-0 text-xs font-medium"
        >
          +{String(items.length - MAX_CHIPS_PER_FIELD)} more
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

/** Product detail page — shows full enrichment, overview, and optimization comparison. */
export function ProductDetailPage({
  batchId,
  productId,
  from,
  tab,
  hideEnrichment,
  detailTab,
  chrome = 'page',
  batchReturn,
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const { t: tCommon } = useTranslation('common');
  // Both are module-level components, so swapping between them never remounts
  // the subtree for a reason other than the host genuinely changing.
  const Shell = chrome === 'modal' ? ModalShell : CatalogOperationsLayout;
  const [activeTab, setActiveTab] = useState<ProductDetailTab>(detailTab ?? 'product-detail');
  // PDP-preview mode: hides the enrichment score and the "Current" (pre-enrichment)
  // panels so the page reads as the final enriched product only.
  const [pdpView, setPdpView] = useState(false);
  // One-way expand for the Tags/Trends chip lists — once the user reveals the
  // rest via "+x more" there's no need to collapse it back.
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [trendsExpanded, setTrendsExpanded] = useState(false);
  // Optimization list filters — impact severity (multi-select) and free-text
  // search across attribute label + current/optimized values.
  const [impactFilter, setImpactFilter] = useState<string[]>([]);
  const [attributeFilter, setAttributeFilter] = useState<string[]>([]);
  // Manual expand/collapse overrides, keyed by `<filter signature>::<row key>`.
  // Folding the filter selection into the key means a new selection automatically
  // discards the previous selection's overrides — no effect needed to reset them.
  const [manualRowOpen, setManualRowOpen] = useState<Record<string, boolean>>({});
  // Fallback open state for rows without a manual override — flipped by the
  // expand/collapse-all toggle. Starts expanded because the before/after
  // comparison is what this page is for.
  const [rowOpenDefault, setRowOpenDefault] = useState(true);
  // Which version the reader is looking at. `null` = the live row, which is the
  // default and the only state the rest of the page knew about before.
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  const navigate = useNavigate();

  // Manual refresh: every query on this page lives under the catalog-operations
  // key subtree, so invalidating it once refetches all of them (detail,
  // enrichment, edits, gap analysis, descriptors, tags/trends) in one action.
  const queryClient = useQueryClient();

  function handleTabChange(next: ProductDetailTab) {
    setActiveTab(next);
    // In a modal the URL belongs to the host route (a funnel step), not to this
    // view — writing `detailTab` there would leave a stale param behind after
    // the dialog closes, and on a guarded route it can trigger a re-validation.
    if (chrome === 'modal') return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
    void (navigate as any)({
      search: (prev: Record<string, unknown>) => ({ ...prev, detailTab: next }),
      replace: true,
    });
  }

  // Sibling navigation, resolved off the products listing's own query. Yields
  // nothing (and so hides the pager) unless the shared listing store is scoped to
  // this batch — which rules out deep links and the cross-batch needs-attention
  // listing, whose state lives in local component state instead of that store.
  const {
    position: siblingPosition,
    total: siblingTotal,
    previous: previousSibling,
    next: nextSibling,
    isLoadingEdge: isLoadingSiblingEdge,
  } = useProductSiblings({ batchId, productId, tab });
  const setListingPagination = useProductsListingStore((s) => s.setPagination);

  /** Moves to a sibling product, keeping the listing store's page in step so
   *  returning via the breadcrumb lands on the page that product sits on. */
  function goToSibling(sibling: ProductSibling) {
    if (sibling.page !== useProductsListingStore.getState().pagination.page) {
      setListingPagination({
        page: sibling.page,
        pageSize: useProductsListingStore.getState().pagination.pageSize,
      });
    }
    void navigate({
      to: '/catalog-operations/$batchId/products/$productId',
      params: { batchId: sibling.batchId, productId: sibling.productId },
      search: {
        ...(tab ? { tab } : {}),
        // Carried so the sibling keeps the same breadcrumb variant. Unreachable
        // today (the pager hides when `from` is set, since that route passes an
        // empty batchId), but dropping it would silently break the breadcrumb if
        // the pager is ever enabled there.
        ...(from ? { from } : {}),
        ...(activeTab !== 'product-detail' ? { detailTab: activeTab } : {}),
      },
    });
  }

  const { data: detailRaw, isLoading, isError } = useProductDetail(batchId, productId);

  // Versioning is a backend rollout: until the curation service ships it, the
  // detail payload carries no `version` and the three version routes 404. Treat
  // an absent `version` as "not available here" and keep the whole feature —
  // tab and requests alike — off, rather than showing an empty tab backed by
  // failing calls.
  const versioningAvailable = detailRaw?.version !== undefined;
  // Requests only once the reader is actually on the tab. Without this the two
  // timelines fire on every product-detail view, tab or not, and refire on each
  // window focus.
  const versionsActive = versioningAvailable && activeTab === 'version-history';

  // Feed-product timeline. `productId` here is the curation feed_product_id —
  // the raw connector row — which is the entity `products/{id}/versions`
  // describes. The curated product's own history is a separate call below.
  const {
    data: versionEntries,
    isLoading: versionsLoading,
    isError: versionsError,
  } = useProductVersions(versionsActive ? productId : null);
  // The curated product's timeline. Keyed on the INTERNAL product id, which is
  // only known once detail resolves — never `lilyId`, which can be a feed id.
  const {
    data: internalVersionEntries,
    isLoading: internalVersionsLoading,
    isError: internalVersionsError,
  } = useInternalProductVersions(versionsActive ? (detailRaw.internalProductId ?? null) : null);
  // One archived version, fetched only while the reader is viewing history.
  const { data: historicalDetail, isLoading: historicalLoading } = useProductVersion(
    viewingVersion === null ? null : productId,
    viewingVersion,
  );

  // Batch source (cached from the batch/products screens) tells us whether this
  // is a Feed Audit run — those audit rather than deliver, so the Route below
  // shows the scan-search icon in place of a destination platform. Only reads
  // header fields (`triggerSource`, `searchDescriptorsEnabled`), so the light
  // phase-1 header suffices — no need for the heavy `/jobs/{id}` aggregate.
  const { data: batchDetail } = useBatchHeader(batchId);
  const batchTriggerSource = batchDetail?.triggerSource ?? '';
  const isFeedAudit = isFeedAuditSource(batchTriggerSource);
  const isTrial = isTrialSource(batchTriggerSource);

  // The Search Descriptors tab is only meaningful when the run enabled that
  // step. When the batch's echoed `enrichmentConfig.searchDescriptorsEnabled` is
  // OFF, disable the tab (and skip its fetch below). Only an explicit `false`
  // disables — a null/absent config leaves the tab enabled.
  const searchDescriptorsDisabled = batchDetail?.searchDescriptorsEnabled === false;
  const canTranslate = useCanTranslateContent();
  const isMetaAdsBatch = batchDetail?.useCase === 'META_ADS';
  // Never leave the view pointing at a disabled tab — e.g. a deep link to
  // `?detailTab=search-descriptors` on a run with descriptors off falls back to
  // Product Detail for both the control and the panel below.
  // Languages (the English | en-GB | es-ES comparison): internal users, GMC products only —
  // Meta content is not translated. Dropped, not disabled, for everyone else.
  const languagesAvailable = canTranslate && !isMetaAdsBatch;
  // Search Impact merges the Gap Analysis and Search Descriptors tabs behind a
  // feature gate. On, the legacy values (old deep links, stale URLs) resolve to
  // the merged tab; off, a stray `search-impact` value falls back to Product Detail.
  const { isAllowed: searchImpactEnabled } = useFeatureGate('search_impact_ui');
  const resolvedTab: ProductDetailTab =
    searchImpactEnabled && (activeTab === 'gap-analysis' || activeTab === 'search-descriptors')
      ? 'search-impact'
      : activeTab;
  const effectiveActiveTab: ProductDetailTab =
    (!searchImpactEnabled && resolvedTab === 'search-impact') ||
    (!searchImpactEnabled && searchDescriptorsDisabled && resolvedTab === 'search-descriptors') ||
    (!versioningAvailable && resolvedTab === 'version-history') ||
    (!languagesAvailable && resolvedTab === 'languages')
      ? 'product-detail'
      : resolvedTab;
  // Tab list with the Search Descriptors option disabled — plus a tooltip
  // explaining why — when the run turned that step off. The History tab is
  // dropped outright rather than disabled: it is absent for everyone until the
  // curation service ships versioning, and a permanently greyed tab reads as a
  // per-product state rather than a capability that has not landed.
  // With Search Impact on, the two legacy tabs collapse into the one merged tab,
  // which is never disabled — the ledger half still has data without descriptors.
  const detailTabs = (
    searchImpactEnabled
      ? DETAIL_TABS.filter((tabOption) => tabOption.value !== 'search-descriptors').map(
          (tabOption) => (tabOption.value === 'gap-analysis' ? SEARCH_IMPACT_TAB : tabOption),
        )
      : DETAIL_TABS
  )
    .filter(
      (tabOption) =>
        (tabOption.value !== 'version-history' || versioningAvailable) &&
        (tabOption.value !== 'languages' || languagesAvailable),
    )
    .map((tabOption) =>
      tabOption.value === 'search-descriptors' && searchDescriptorsDisabled
        ? {
            ...tabOption,
            disabled: true,
            tooltip: t('detailHeader.searchDescriptorsDisabledTooltip'),
          }
        : tabOption,
    );

  const connectorCode = detailRaw?.feedSource?.toLowerCase().includes('meta') ? 'META' : undefined;

  // Enrichment lives on a separate endpoint and hydrates the score card +
  // optimization accordion. The global needs-attention view suppresses the
  // call entirely since those products haven't been processed yet.
  const {
    data: enrichment,
    isLoading: isEnrichmentLoading,
    isFetching: isEnrichmentFetching,
    refetch: refetchEnrichment,
  } = useProductEnrichment(
    productId,
    false,
    !hideEnrichment && !!detailRaw && detailRaw.feedSource !== null,
    connectorCode,
    // Opened from a batch: show that batch's result, and say so when it has none.
    batchId || undefined,
  );
  // Enrichments | Language diff inside the Enriched Attributes card — only when this product
  // actually has translations (shares the compare view's query, so no extra request).
  const translationsWorkflowId = enrichment?.workflowId ?? batchDetail?.workflowId ?? '';
  const productTranslations = useProductTranslations(
    translationsWorkflowId,
    productId,
    languagesAvailable,
  );
  const hasTranslations = (productTranslations.data?.length ?? 0) > 0;
  const [attributesView, setAttributesView] = useState<AttributeCardView>('enrichments');
  const effectiveAttributesView: AttributeCardView = hasTranslations
    ? attributesView
    : 'enrichments';
  // On-demand data-scoring for this product. The score path wants the batch's
  // workflow slug (the header's `workflowId`), NOT the route's job uuid — read it
  // off the already-fetched header. Empty until it resolves, which gates scoring off.
  const scoreWorkflowId = batchDetail?.workflowId ?? '';
  // Offer scoring only for an actually-enriched product (the `>cap` tail carries
  // optimizations/enriched-at but no batch score) once a workflow id has resolved.
  const canScore =
    !!scoreWorkflowId && (!!enrichment?.enrichedAt || (enrichment?.optimizations?.length ?? 0) > 0);
  const onDemandScore = useProductOnDemandScore({
    workflowId: scoreWorkflowId,
    productId,
    connectorCode,
    // With no batch score to show, read back any existing on-demand score first,
    // so the operator only falls to Generate when there genuinely is none.
    autoFetch: canScore && !enrichment?.enrichmentScore && !isEnrichmentLoading,
  });

  // Operator-override provenance. Same enable gate and connector as enrichment,
  // since it annotates those same rows — and it is fetched up-front rather than on
  // expand because the "Edited" marker sits on collapsed rows.
  const { data: attributeEdits } = useProductAttributeEdits(
    productId,
    !hideEnrichment && !!detailRaw && detailRaw.feedSource !== null,
    connectorCode,
  );

  // Fired in parallel with enrichment (same enable gate + connector) rather than
  // gated behind the Gap Analysis tab, so it's already resolved by the time the
  // user switches tabs.
  // Additionally gated on `chrome`: the modal hides the tab switcher (see the
  // TODO below), and this endpoint is read-through — a first read *starts* the
  // agent, so firing it for a tab nobody can reach spends backend work, not just
  // a request. Re-enable together with the tabs.
  const {
    data: gapAnalysis,
    isLoading: isGapAnalysisLoading,
    isError: isGapAnalysisError,
  } = useProductGapAnalysis(
    productId,
    chrome === 'page' &&
      !searchImpactEnabled &&
      !hideEnrichment &&
      !!detailRaw &&
      detailRaw.feedSource !== null,
  );
  const refreshGapAnalysis = useRefreshProductGapAnalysis(productId);
  // Merged Search Impact payload, pinned to this batch (results are per producer) —
  // only when the gate is on.
  const {
    data: searchImpact,
    isLoading: isSearchImpactLoading,
    isError: isSearchImpactError,
  } = useProductSearchImpact(
    productId,
    batchId || undefined,
    searchImpactEnabled &&
      chrome === 'page' &&
      !hideEnrichment &&
      !!detailRaw &&
      detailRaw.feedSource !== null,
  );
  const refreshSearchImpact = useRefreshProductSearchImpact(productId, batchId || undefined);

  // Same enable gate again, so the tab has its data before the user reaches it.
  // A plain read, unlike gap analysis — no polling, and `null` (the contract's
  // 404) is a successful "never generated" answer rather than an error.
  //
  // `batchId` is the enrichment job id, and pinning the read to it matters: the
  // descriptors are stored per producer, so a product enriched by a later batch
  // would otherwise resolve to that batch's descriptors while this page is
  // showing this one. Empty on the global needs-attention route, where the query
  // is disabled anyway.
  // The query also drives the Product Description row's Description / Search
  // Descriptors / Phrase Descriptions toggle — shared with the quick view.
  const {
    query: {
      data: searchDescriptors,
      isLoading: isSearchDescriptorsLoading,
      isError: isSearchDescriptorsError,
    },
    descriptorViewsFor,
  } = useDescriptionDescriptorViews({
    productId,
    enrichmentJobId: batchId || undefined,
    // Same `chrome` gate as gap analysis — its tab is unreachable in a modal.
    // Also skipped when the run disabled search descriptors, since the tab is off.
    enabled: chrome === 'page' && !hideEnrichment && !!detailRaw && detailRaw.feedSource !== null,
    searchDescriptorsDisabled,
  });
  // Fetch tags & trends for this single product. batchId is the job uuid, or the
  // experiment id for experiment batches; waits for the header to tell which.
  const { data: tagsTrendsMap } = useProductTagsTrends(
    detailRaw && batchDetail ? tagsTrendsScope(batchId, batchTriggerSource) : undefined,
    [productId],
  );
  const tagsTrends = tagsTrendsMap?.get(productId);

  // Merge enrichment slice on top of the curation overview, then hydrate each
  // optimization's "Current" panel from the curation `rawFields` map (the two
  // APIs return different shapes — the mapper handles field-name aliasing).
  // Explicit annotation avoids ESLint no-unsafe-* false positives from the spread-object union.
  const detail: ProductDetail | undefined =
    detailRaw && enrichment
      ? ({
          ...detailRaw,
          ...enrichment,
          // Live curation feed values win; the enrichment row's snapshot (what the
          // run saw) only fills in when curation hasn't caught up yet.
          sourceShipping: detailRaw.sourceShipping ?? enrichment.sourceShipping,
          optimizations: applyCurrentValues(
            enrichment.optimizations ?? [],
            detailRaw.rawFields,
          ).map((opt) =>
            opt.contentType === 'product-details'
              ? { ...opt, currentAttributes: detailRaw.productDetailsStructured ?? [] }
              : opt,
          ),
        } as ProductDetail)
      : detailRaw;

  // META_ADS views drop Retailer Id / Image Link and show the merchant's Price and
  // Sale Price read-only — the rows, the impact filter, and the attribute dropdown
  // all read from this.
  const isMetaAds = batchDetail?.useCase === 'META_ADS';
  const visibleOptimizations = useMemo(() => {
    const all = detail?.optimizations ?? [];
    if (!isMetaAds) return all;
    return toMetaVisibleOptimizations(all);
  }, [detail?.optimizations, isMetaAds]);

  // Impact values actually present in this product's optimizations — drives
  // the filter dropdown so users never see an option with zero matches.
  const impactOptions = useMemo(() => {
    const impacts = new Set<string>();
    visibleOptimizations.forEach((opt) => {
      if (opt.impact) impacts.add(opt.impact);
    });
    return sortImpacts([...impacts]);
  }, [visibleOptimizations]);

  // Stable React key per optimization, assigned from the UNFILTERED list. Each
  // row owns its own expand/collapse state, so keying on the filtered position
  // would remount rows whenever the filters re-index the visible set —
  // collapsing whatever the user had open. `field` alone isn't unique (the API
  // can return the same field twice), hence the index tie-break.
  const identifiedOptimizations = useMemo(
    () =>
      // Severity first (Critical → High → Medium → …), then name, so the
      // attributes that matter most sit at the top of the accordion. Keys are
      // assigned after sorting but from the sorted position, which is stable for
      // a given payload.
      sortByImpact(visibleOptimizations).map((opt, i) => ({
        opt,
        key: `${opt.field}-${String(i)}`,
      })),
    [visibleOptimizations],
  );

  // Every attribute name on the product, for the attribute combo box. Sorted
  // alphabetically so the dropdown order matches the rows underneath it.
  const attributeOptions = useMemo(() => {
    const labels = new Set<string>();
    visibleOptimizations.forEach((opt) => {
      if (opt.label) labels.add(opt.label);
    });
    return [...labels].sort((a, b) => a.localeCompare(b));
  }, [visibleOptimizations]);

  // Counts produced by enrichment, in the order they appear in the signals
  // column. `trends` prefers the per-product tags/trends payload when it has
  // resolved, since it's the same source the chips above are drawn from.
  const signalRows = [
    {
      label: 'Attributes',
      count: detail?.attributeCount ?? 0,
      isLoading: isEnrichmentLoading,
      // No field: clears the filter and shows the whole list.
      field: null,
      target: 'attributes' as const,
    },
    {
      // Counts entries in the `product_detail` enrichment field. Deliberately
      // NOT called "search descriptors" — that name belongs to the ranked
      // keyword phrases on the Search Descriptors tab, which are unrelated data
      // from a different endpoint.
      label: 'Product details',
      count: detail?.searchDescriptorCount ?? 0,
      isLoading: isEnrichmentLoading,
      field: 'product_detail',
      target: 'attributes' as const,
    },
    {
      label: 'Highlights',
      count: detail?.highlightCount ?? 0,
      isLoading: isEnrichmentLoading,
      field: 'product_highlight',
      target: 'attributes' as const,
    },
    {
      label: 'Trends',
      count: tagsTrends?.trends.length ?? detail?.trendCount ?? 0,
      field: null,
      target: 'trends' as const,
    },
  ];
  // Shared bar scale — one outlier shouldn't be measured against itself.
  const signalMax = Math.max(...signalRows.map((row) => row.count), 1);

  const updateAttribute = useUpdateProductAttribute(productId, connectorCode);
  // Editing only makes sense on an enriched product — a needs-attention item has
  // no Lily value to override, and the global needs-attention view skips
  // enrichment entirely.
  const canEditAttributes = !hideEnrichment && detail?.status !== 'needs-attention';

  // Provenance keyed by field, so each row is O(1) to annotate. Keyed off the
  // enrichment's own `workflowId`: an edit recorded against an earlier run was
  // reverted when that run's values were replaced, so it is history rather than
  // the override in effect.
  const editProvenance = useMemo(
    () => indexAttributeEdits(attributeEdits ?? [], enrichment?.workflowId),
    [attributeEdits, enrichment?.workflowId],
  );

  const attributesSectionRef = useRef<HTMLDivElement>(null);

  /** Reveals the data behind a summary count: narrows the attribute list to the
   *  matching optimization and scrolls to it, or expands the trends chips. */
  function revealSignal(row: (typeof signalRows)[number]) {
    if (row.target === 'trends') {
      setTrendsExpanded(true);
      return;
    }
    // Resolve the label from the data — field names are stable, labels are not.
    const match = row.field
      ? identifiedOptimizations.find((entry) => entry.opt.field === row.field)
      : undefined;
    setAttributeFilter(match ? [match.opt.label] : []);
    setImpactFilter([]);
    attributesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const attributeSignature = attributeFilter.join('|');

  // Optimizations narrowed by the two combo boxes. Both are AND-ed, and an empty
  // selection means "no constraint" rather than "match nothing".
  const filteredOptimizations = useMemo(
    () =>
      identifiedOptimizations.filter(({ opt }) => {
        if (impactFilter.length > 0 && (!opt.impact || !impactFilter.includes(opt.impact))) {
          return false;
        }
        if (attributeFilter.length > 0 && !attributeFilter.includes(opt.label)) {
          return false;
        }
        return true;
      }),
    [identifiedOptimizations, impactFilter, attributeFilter],
  );

  // Drives the expand/collapse-all toggle's label: as long as one visible row is
  // open the toggle collapses, and only once every row is shut does it expand.
  const anyRowExpanded = filteredOptimizations.some(
    ({ key }) => manualRowOpen[`${attributeSignature}::${key}`] ?? rowOpenDefault,
  );

  /** Opens or shuts every visible attribute row at once, clearing the per-row
   *  overrides so "all" actually means all rather than all-but-the-toggled-ones. */
  function toggleAllRows() {
    setRowOpenDefault(!anyRowExpanded);
    setManualRowOpen({});
  }

  /** Exports whatever the filters currently show as JSON. `scope` selects which
   *  side of the comparison is written, so a "current only" export can be diffed
   *  against the retailer's own feed without the enriched values in the way. */
  function downloadAttributes(scope: AttributeExportScope) {
    const payload = {
      productId,
      title: detail?.title ?? null,
      lilyId: detail?.lilyId ?? null,
      scope,
      // Recorded so an export is reproducible — a partial file is otherwise
      // indistinguishable from a product that only has these attributes.
      filters: { impact: impactFilter, attributes: attributeFilter },
      attributeCount: filteredOptimizations.length,
      attributes: filteredOptimizations.map(({ opt }) => ({
        field: opt.field,
        label: opt.label,
        impact: opt.impact ?? null,
        contentType: opt.contentType,
        ...(scope === 'post' ? {} : { current: opt.currentValue ?? null }),
        ...(scope === 'pre' ? {} : { enriched: opt.value }),
      })),
    };
    const stem =
      (detail?.title ?? productId)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 60) || 'product';
    triggerBrowserDownload(
      new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
      `${stem}-attributes-${scope}.json`,
    );
  }

  // Breadcrumb + sibling pager, rendered above EVERY state (loading, error,
  // loaded). Paging between products only swaps the body below — this chrome
  // stays put and the pager keeps working — instead of the whole page blanking
  // to a skeleton on each navigation. Page chrome only: a modal host owns its
  // own frame and has no route to page siblings through.
  const breadcrumbAndPager =
    chrome === 'page' ? (
      <div
        data-testid="product-detail-header-nav"
        className="flex items-start justify-between gap-4"
      >
        <nav
          data-testid="product-detail-breadcrumb"
          className="flex min-w-0 flex-wrap items-center gap-1.5 text-sm"
        >
          <Link
            data-testid="product-detail-breadcrumb-catalog-operations-link"
            to="/catalog-operations"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors"
          >
            <Zap className="h-4 w-4 text-amber-500" aria-hidden="true" />
            <span>Catalog Operations</span>
          </Link>
          <ChevronRight className="text-muted-foreground h-4 w-4" aria-hidden="true" />
          {from === 'needs-attention' ? (
            <Link
              data-testid="product-detail-breadcrumb-needs-attention-link"
              to="/catalog-operations/products-need-attention"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Products Needing Attention
            </Link>
          ) : from === 'batch-detail' ? (
            <Link
              data-testid="product-detail-breadcrumb-batch-link"
              to="/catalog-operations/$batchId"
              params={{ batchId }}
              search={{
                ...(batchReturn?.tab ? { tab: batchReturn.tab } : {}),
                ...(batchReturn?.membership
                  ? {
                      membership: batchReturn.membership.category,
                      membershipLabel: batchReturn.membership.label,
                      ...(batchReturn.membership.group
                        ? { membershipGroup: batchReturn.membership.group }
                        : {}),
                    }
                  : {}),
              }}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Batch Details
            </Link>
          ) : (
            <>
              <Link
                data-testid="product-detail-breadcrumb-batch-link"
                to="/catalog-operations/$batchId"
                params={{ batchId }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Batch Details
              </Link>
              <ChevronRight className="text-muted-foreground h-4 w-4" aria-hidden="true" />
              <Link
                data-testid="product-detail-breadcrumb-products-link"
                to="/catalog-operations/$batchId/products"
                params={{ batchId }}
                search={tab ? { tab } : {}}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Products
              </Link>
            </>
          )}
          <ChevronRight className="text-muted-foreground h-4 w-4" aria-hidden="true" />
          <span data-testid="product-detail-breadcrumb-current" className="font-semibold">
            Product Details
          </span>
        </nav>
        {/* No pager from the insight drawer — its siblings aren't the listing's. */}
        {from !== 'batch-detail' && siblingPosition !== null && siblingTotal > 1 && (
          <ProductPager
            position={siblingPosition}
            total={siblingTotal}
            isLoading={isLoadingSiblingEdge}
            onPrevious={
              previousSibling
                ? () => {
                    goToSibling(previousSibling);
                  }
                : undefined
            }
            onNext={
              nextSibling
                ? () => {
                    goToSibling(nextSibling);
                  }
                : undefined
            }
          />
        )}
      </div>
    ) : null;

  if (isLoading) {
    return (
      <Shell testId={chrome === 'modal' ? 'product-detail-modal-layout' : 'product-detail-layout'}>
        <div data-testid="product-detail-page" className="w-full min-w-0 space-y-6">
          {breadcrumbAndPager}
          {/* Title + tabs placeholder (page chrome only — a modal renders its
              own title, so its loading state is just the body skeletons). */}
          {chrome === 'page' && (
            <div className="flex flex-col gap-4 min-[1440px]:flex-row min-[1440px]:items-center min-[1440px]:justify-between">
              <div className="min-w-0 space-y-2">
                <Skeleton className="h-7 w-80 max-w-full rounded-lg" />
                <Skeleton className="h-4 w-96 max-w-full rounded" />
              </div>
              <Skeleton className="h-11 w-72 max-w-full rounded-lg" />
            </div>
          )}
          {/* Overview card + attribute rows */}
          <Skeleton data-testid="product-detail-loading" className="h-120 w-full rounded-2xl" />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-2xl" />
            ))}
          </div>
        </div>
      </Shell>
    );
  }

  if (isError || detail === undefined) {
    return (
      <Shell testId={chrome === 'modal' ? 'product-detail-modal-layout' : 'product-detail-layout'}>
        <div data-testid="product-detail-page" className="w-full min-w-0 space-y-6">
          {breadcrumbAndPager}
          <div className="flex min-h-[40vh] items-center justify-center">
            <p data-testid="product-detail-error" className="text-muted-foreground text-sm">
              Failed to load product details. Please try again.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  const statusCfg = STATUS_CONFIG[detail.status];
  const StatusIcon = statusCfg.icon;
  // `externalId` only arrives via the enrichment endpoint. Default to hidden
  // (rather than flashing the section while enrichment is still loading) and
  // only reveal it once we've confirmed the product isn't a trial/demo one
  // (synthetic `accounts/trial/...` externalId).
  const showSourceDestination =
    !isEnrichmentLoading && !(detail.externalId?.includes('accounts/trial') ?? false);
  // Dedicated tags/trends endpoint takes priority; falls back to the curation
  // overview's snapshot until it resolves.
  const tags = tagsTrends?.tags ?? detail.tags;
  const trends = tagsTrends?.trends ?? detail.trends;

  // get max score out of before and after
  const scoreBaseFactor = Math.max(
    detail.enrichmentScore?.before ?? 0,
    detail.enrichmentScore?.after ?? 0,
  );
  // determine if the score is out of 10 or 100. 10 -> 100, 1 -> 10
  const scoreBase = scoreBaseFactor > 10 ? 10 : 1;

  // A score generated on demand this session takes precedence over the batch
  // `enrichmentScore` (which the on-demand endpoint never writes back to). Both
  // feed the meter on the 0–10 scale — on-demand values are already 0–10, the
  // batch score is normalised via `scoreBase`.
  const onDemandPre = onDemandScore.result?.pre?.overallScore;
  const onDemandPost = onDemandScore.result?.post?.overallScore;
  const hasOnDemandPair = onDemandPre != null && onDemandPost != null;
  const scoreBefore = hasOnDemandPair
    ? onDemandPre
    : detail.enrichmentScore
      ? detail.enrichmentScore.before / scoreBase
      : undefined;
  const scoreAfter = hasOnDemandPair
    ? onDemandPost
    : detail.enrichmentScore
      ? detail.enrichmentScore.after / scoreBase
      : undefined;
  const scorePhase = onDemandScore.isChecking
    ? 'loading'
    : onDemandScore.status === 'FAILED'
      ? 'failed'
      : onDemandScore.status === 'PENDING'
        ? 'pending'
        : 'idle';

  return (
    <Shell testId={chrome === 'modal' ? 'product-detail-modal-layout' : 'product-detail-layout'}>
      <div
        data-testid="product-detail-page"
        className={cn(
          'min-w-0',
          chrome === 'page' ? 'flex h-full min-h-0 flex-col' : 'w-full space-y-6',
        )}
      >
        {/* Pinned header — the breadcrumb/pager + title/tab switcher stay put
            while the tab content below scrolls in its own region. Page chrome
            only: a modal host renders its own title/frame (and the tab switcher
            is suppressed there), so both are absent in that variant.
            TODO: restore the tab switcher in modal chrome — it collides with the
            dialog's close button and Gap Analysis / Search Descriptors haven't
            been reviewed inside the modal; restoring means re-enabling the two
            queries gated on `chrome` above. */}
        {chrome === 'page' && (
          <div className="shrink-0 space-y-6 pb-6">
            {breadcrumbAndPager}

            {/* Below 1440px the title/subtitle can crowd the action row, so the
                actions drop onto their own line beneath the heading (toggle +
                refresh left, tabs right); at ≥1440px they sit inline on the right. */}
            <div className="flex flex-col gap-4 min-[1440px]:flex-row min-[1440px]:items-center min-[1440px]:justify-between">
              <div className="min-w-0">
                <h1
                  data-testid="product-detail-title"
                  className="text-lg font-medium tracking-tight break-words"
                >
                  {detail.title}
                </h1>
                <p
                  data-testid="product-detail-retailer-product-id"
                  className="text-muted-foreground text-sm font-[400] break-all"
                >
                  Retailer Product ID: {detail.retailerProductId}
                </p>
              </div>
              <div className="flex items-center justify-between gap-3 min-[1440px]:shrink-0 min-[1440px]:justify-end">
                {/* Left cluster: PDP-preview toggle + manual refresh + delivery history link. */}
                <div className="flex items-center gap-3">
                  {/* PDP-preview toggle — enriched-only view. Only meaningful on the
                    Product Detail tab of an enriched product. */}
                  {effectiveActiveTab === 'product-detail' &&
                    detail.status !== 'needs-attention' && (
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor="pdp-view-toggle"
                          className="text-muted-foreground cursor-pointer text-sm"
                        >
                          {t('detailHeader.pdpView')}
                        </Label>
                        <InfoTooltip
                          content={t('detailHeader.pdpViewTooltip')}
                          testId="product-detail-pdp-view-info-tooltip"
                        />
                        <Switch
                          data-testid="product-detail-pdp-view-toggle"
                          id="pdp-view-toggle"
                          checked={pdpView}
                          onCheckedChange={setPdpView}
                          aria-label={t('detailHeader.pdpView')}
                          className="cursor-pointer"
                        />
                      </div>
                    )}
                  {/* Refreshes whatever the active tab shows. Gap analysis needs a
                    recompute rather than a refetch — its result is cached with no
                    TTL, so invalidating the query would just re-read the same
                    answer. Every other tab is served by a plain refetch, which one
                    invalidation of the catalog-operations subtree covers. */}
                  <RefreshIconButton
                    testId="product-detail-refresh-button"
                    label={t('detailHeader.refresh')}
                    onRefresh={() => {
                      if (effectiveActiveTab === 'gap-analysis') {
                        return refreshGapAnalysis.mutateAsync().then(() => undefined);
                      }
                      // Search Impact is stored, not live — recompute it the same way.
                      if (effectiveActiveTab === 'search-impact') {
                        return refreshSearchImpact.mutateAsync().then(() => undefined);
                      }
                      return queryClient.invalidateQueries({
                        queryKey: queryKeys.catalogOperations.all,
                      });
                    }}
                  />
                  <ProductDeliveryHistoryLink
                    batchConnectorCode={batchDetail?.connectorCode}
                    feedSource={detail.feedSource}
                    externalId={detail.externalId}
                    sku={detail.sku || detail.retailerProductId}
                    liveVersion={detailRaw?.version}
                    batchStatus={batchDetail?.status}
                    batchId={batchId}
                    productId={productId}
                  />
                </div>
                {detail.status !== 'needs-attention' && (
                  <SegmentedTabs
                    value={effectiveActiveTab}
                    onChange={handleTabChange}
                    options={detailTabs}
                    ariaLabel="Product detail sections"
                    testId="product-detail-tabs"
                    panelId="product-detail-panel"
                    className="flex w-auto text-nowrap sm:max-w-none"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <div className={chrome === 'page' ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
          <div data-testid="product-detail-panel" role="tabpanel" id="product-detail-panel">
            {effectiveActiveTab === 'product-detail' && (
              <div className="space-y-6">
                {detail.inBatch === false && (
                  <div
                    role="status"
                    data-testid="product-detail-not-in-batch"
                    className="bg-warning-50 border-warning/40 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm"
                  >
                    <TriangleAlert
                      className="text-warning mt-0.5 size-4 shrink-0"
                      aria-hidden="true"
                    />
                    <p>
                      <span className="font-semibold">Not enriched in this batch.</span>{' '}
                      {detail.enrichedAt
                        ? `Showing this product's latest Lily AI content, from ${formatDateTime(detail.enrichedAt)} — another batch.`
                        : "Showing this product's latest Lily AI content, from another batch."}
                    </p>
                  </div>
                )}
                {/* Overview card */}
                <div
                  data-testid="product-detail-overview-card"
                  className="overflow-hidden rounded-2xl border bg-white shadow-sm"
                >
                  <ResizableSplit
                    defaultLeftPct={25}
                    minLeftPct={20}
                    maxLeftPct={55}
                    left={
                      <div
                        data-testid="product-detail-gallery"
                        className="min-w-0 border-b p-5 lg:border-r lg:border-b-0"
                      >
                        <ProductImageGallery images={detail.images} title={detail.title} />
                      </div>
                    }
                    right={
                      <div className="@container min-w-0">
                        {/* Card header carries the status, which removes the separate
                          Product Status box that used to sit below the panel. */}
                        <div className="flex items-center justify-between gap-3 px-4 py-3">
                          <h2 className="text-base font-semibold">Overview</h2>
                          <Badge
                            data-testid="product-detail-status-badge"
                            variant="outline"
                            className={cn('gap-1.5 rounded-md border-0', statusCfg.className)}
                          >
                            <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />
                            {statusCfg.label}
                          </Badge>
                        </div>

                        {/* Two jobs, two columns: what the product is, and what
                          enrichment produced. Container query (not a viewport
                          breakpoint) because this pane is user-resizable. In the
                          PDP-preview view the enrichment column drops and identity
                          spans full width. */}
                        <div
                          className={cn(
                            'grid grid-cols-1 border-y transition-[grid-template-columns] duration-300 ease-in-out motion-reduce:transition-none',
                            pdpView
                              ? '@2xl:grid-cols-[minmax(0,1fr)_0fr]'
                              : '@2xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]',
                          )}
                        >
                          {/* Identity */}
                          <div className="min-w-0 divide-y @2xl:border-r">
                            <IdentityRow label="Lily ID">
                              <TailSafeText value={detail.lilyId} className="font-mono text-xs" />
                              <CopyIconButton
                                text={detail.lilyId}
                                label="Copy Lily ID"
                                testId="product-detail-lily-id-copy-button"
                                copyMessage="Copied Lily ID"
                              />
                              {/* Langfuse traces for this product. Last in the row
                                so the id and its copy control stay adjacent, and
                                self-hiding when there's no id to link. */}
                              <ExternalLinkIconButton
                                href={langfuseUserUrl(detail.lilyId)}
                                label="View traces in Langfuse"
                                testId="product-detail-langfuse-link"
                              />
                            </IdentityRow>
                            <IdentityRow label="External Id">
                              <TailSafeText value={detail.sku} className="font-mono text-xs" />
                              <CopyIconButton
                                text={detail.sku}
                                label="Copy External Id"
                                testId="product-detail-external-id-copy-button"
                                copyMessage="Copied External Id"
                              />
                            </IdentityRow>
                            <IdentityRow label="Brand">
                              <span className="truncate text-sm font-semibold" title={detail.brand}>
                                {detail.brand}
                              </span>
                            </IdentityRow>
                            <IdentityRow label="Category">
                              <ProductCategory
                                value={detail.retailerCategory}
                                testId="product-detail-category"
                              >
                                {(display) => <CategoryPath value={display} />}
                              </ProductCategory>
                            </IdentityRow>
                            {detail.enrichedAt && (
                              <IdentityRow label="Enriched">
                                <span className="text-sm font-semibold tabular-nums">
                                  {formatDateTime(detail.enrichedAt)}{' '}
                                  <span className="text-muted-foreground text-xs font-normal">
                                    ({getTimeZoneAbbrev(detail.enrichedAt)})
                                  </span>
                                </span>
                              </IdentityRow>
                            )}
                            {showSourceDestination && (
                              <IdentityRow label="Route">
                                <span className="text-secondary-text flex min-w-0 items-center gap-2">
                                  <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                  {isTrial ? (
                                    <FileText
                                      className="text-muted-foreground h-4 w-4 shrink-0"
                                      aria-label="Trial"
                                    />
                                  ) : (
                                    PLATFORM_LOGO[detail.sourcePlatform] && (
                                      <img
                                        src={PLATFORM_LOGO[detail.sourcePlatform]}
                                        alt={detail.sourcePlatform}
                                        className="h-4 w-4 shrink-0 object-contain"
                                      />
                                    )
                                  )}
                                  <img
                                    src={sourceDestinationArrow}
                                    alt=""
                                    className="h-auto w-10 shrink-0 opacity-60"
                                  />
                                  {isFeedAudit ? (
                                    <ScanSearch
                                      className="text-accent-main h-4 w-4 shrink-0"
                                      aria-label="Feed Audit"
                                    />
                                  ) : isTrial ? (
                                    <FileText
                                      className="text-muted-foreground h-4 w-4 shrink-0"
                                      aria-label="Trial"
                                    />
                                  ) : (
                                    PLATFORM_LOGO[detail.destinationPlatform] && (
                                      <img
                                        src={PLATFORM_LOGO[detail.destinationPlatform]}
                                        alt={detail.destinationPlatform}
                                        className="h-4 w-4 shrink-0 object-contain"
                                      />
                                    )
                                  )}
                                  <Flag className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                </span>
                              </IdentityRow>
                            )}
                            <IdentityRow label="Trends">
                              <ChipField
                                items={trends}
                                expanded={trendsExpanded}
                                onExpand={() => {
                                  setTrendsExpanded(true);
                                }}
                                emptyLabel="None generated"
                                testId="product-detail-trends"
                                className="border-purple-200 bg-purple-50 text-purple-700"
                              />
                            </IdentityRow>
                            <IdentityRow label="Tags">
                              <ChipField
                                items={tags}
                                expanded={tagsExpanded}
                                onExpand={() => {
                                  setTagsExpanded(true);
                                }}
                                emptyLabel="None generated"
                                testId="product-detail-tags"
                                className="border-tag-chip-border bg-tag-chip-bg text-tag-chip-text"
                              />
                            </IdentityRow>
                          </div>

                          {/* Enrichment output — collapses out in the PDP-preview view
                            (height when the panel is stacked, width via the grid
                            column above at ≥2xl). */}
                          <div
                            className={cn(
                              'grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none @2xl:block',
                              pdpView ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
                            )}
                            aria-hidden={pdpView}
                          >
                            <div
                              data-testid="product-detail-enrichment-summary"
                              className="bg-surface-blue divide-y overflow-hidden border-t @2xl:border-t-0"
                            >
                              <div className="px-4 py-2">
                                <span className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
                                  Enrichment summary
                                </span>
                              </div>
                              {signalRows.map((row) => (
                                <SignalRow
                                  key={row.label}
                                  testId={`product-detail-signal-${row.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  label={row.label}
                                  count={row.count}
                                  max={signalMax}
                                  isLoading={row.isLoading}
                                  onSelect={() => {
                                    revealSignal(row);
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Enrichment score lives here rather than in its own card:
                          the panel is as tall as the image beside it, and this was
                          the space going spare. Collapses out in the PDP-preview view. */}
                        <div
                          className={cn(
                            'grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none',
                            pdpView ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
                          )}
                          aria-hidden={pdpView}
                        >
                          <div className="overflow-hidden">
                            {isEnrichmentLoading ? (
                              <div data-testid="product-detail-score-loading" className="px-4 py-4">
                                <Skeleton className="mb-3 h-4 w-32 rounded" />
                                <Skeleton className="h-20 w-full max-w-3xl rounded-xl" />
                              </div>
                            ) : (
                              // Width capped at max-w-3xl: at full pane width the
                              // bars stretch to ~700px each and read as empty
                              // track. Renders the batch score, an on-demand score,
                              // or a Generate control (and nothing for an unscored,
                              // unscorable product).
                              <EnrichmentScoreSection
                                beforeScore={scoreBefore}
                                afterScore={scoreAfter}
                                canScore={canScore}
                                isBusy={onDemandScore.isBusy}
                                phase={scorePhase}
                                onScore={onDemandScore.trigger}
                                containerClassName="px-4 py-4"
                                meterWrapperClassName="max-w-3xl"
                                meterClassName="border"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    }
                  />
                </div>

                {/* GMC merchant-feed shipping/logistics — renders nothing when the
                    feed states none of the attributes (always for Meta/Shopify). */}
                <SourceShippingSection
                  attributes={detail.sourceShipping}
                  testId="product-detail-source-shipping"
                />

                {/* Optimization sections — gated on the (separate) enrichment API. */}
                {isEnrichmentLoading ? (
                  <div
                    data-testid="product-detail-attributes-loading"
                    className="divide-y overflow-hidden rounded-2xl border bg-white shadow-sm"
                  >
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="p-4">
                        <Skeleton className="h-16 w-full rounded-xl" />
                      </div>
                    ))}
                  </div>
                ) : (
                  detail.optimizations &&
                  detail.optimizations.length > 0 && (
                    <div
                      data-testid="product-detail-attributes"
                      ref={attributesSectionRef}
                      className="divide-y overflow-hidden rounded-2xl border bg-white shadow-sm"
                    >
                      <AttributeFilterCard
                        impactOptions={impactOptions}
                        selectedImpacts={impactFilter}
                        onImpactsChange={setImpactFilter}
                        attributeOptions={attributeOptions}
                        selectedAttributes={attributeFilter}
                        onAttributesChange={setAttributeFilter}
                        anyRowExpanded={anyRowExpanded}
                        onToggleAllRows={toggleAllRows}
                        onRefresh={() => void refetchEnrichment()}
                        isRefreshing={isEnrichmentFetching}
                        onDownload={downloadAttributes}
                        {...(hasTranslations && {
                          view: effectiveAttributesView,
                          onViewChange: setAttributesView,
                        })}
                      />
                      {effectiveAttributesView === 'languages' ? (
                        <div data-testid="product-detail-attributes-languages" className="p-4">
                          <LanguageCompareView
                            workflowId={translationsWorkflowId}
                            productId={productId}
                          />
                        </div>
                      ) : filteredOptimizations.length > 0 ? (
                        filteredOptimizations.map(({ opt, key }) => (
                          // No `disableToggle`: each attribute collapses so a product with many
                          // optimizations stays scannable instead of rendering every diff at once.
                          // `key` is filter-independent — see `identifiedOptimizations`.
                          <EnrichedAttributeRow
                            key={key}
                            opt={opt}
                            // Rows start expanded — the comparison is what the page is
                            // for, and opening each attribute to read it made the common
                            // case a click per row. A manual collapse still wins over
                            // the expand/collapse-all default, and the override key folds
                            // in the filter signature so changing the filter discards the
                            // previous selection's overrides.
                            open={manualRowOpen[`${attributeSignature}::${key}`] ?? rowOpenDefault}
                            onOpenChange={(next) => {
                              setManualRowOpen((m) => ({
                                ...m,
                                [`${attributeSignature}::${key}`]: next,
                              }));
                            }}
                            // Two columns, each with its own scrollbar: the
                            // comparison's "Sync scroll" toggle links them by default
                            // so the diff stays level, and the reviewer can unlink to
                            // scroll the Current or Lily AI side on its own.
                            comparisonLayout="panels"
                            // Keep the jump-to-attribute search the aligned layout
                            // gave this page; the compact previews stay search-less.
                            comparisonShowSearch
                            // PDP-preview view hides the "Current" side everywhere.
                            hideCurrent={pdpView}
                            provenance={editProvenance.get(opt.field)}
                            // No toggle at all when the run never enabled search
                            // descriptors — same signal the Search Descriptors tab
                            // itself is disabled on above, just applied here as an
                            // omission instead of a greyed-out option.
                            descriptorViews={descriptorViewsFor(opt)}
                            {...(canEditAttributes &&
                              !opt.readOnly && {
                                onSave: async (value: string) => {
                                  await updateAttribute.mutateAsync({
                                    field: opt.field,
                                    label: opt.label,
                                    value: toAttributePayloadValue(opt.contentType, value),
                                  });
                                },
                                // Scope the spinner to the row actually being saved.
                                // `variables` is typed non-nullish and is always set
                                // while a mutation is pending, so no optional chain.
                                isSaving:
                                  updateAttribute.isPending &&
                                  updateAttribute.variables.field === opt.field,
                              })}
                          />
                        ))
                      ) : (
                        <p
                          data-testid="product-detail-attributes-empty"
                          className="text-muted-foreground p-8 text-center text-sm"
                        >
                          {t('attributeFilter.noResults')}
                        </p>
                      )}
                    </div>
                  )
                )}
              </div>
            )}

            {effectiveActiveTab === 'search-impact' && detail.status !== 'needs-attention' && (
              <SearchImpactTab
                productId={productId}
                batchId={batchId}
                data={searchImpact}
                isLoading={isSearchImpactLoading}
                isError={isSearchImpactError}
                onOpenSourceData={() => {
                  handleTabChange('product-detail');
                }}
              />
            )}

            {effectiveActiveTab === 'gap-analysis' && detail.status !== 'needs-attention' && (
              <div data-testid="product-detail-gap-analysis-tab" className="flex flex-col gap-4">
                {/* Shown only when Google Ads isn't connected — the Competition /
                    Search Volume columns need a Google Ads link. */}
                <GoogleAdsConnectBanner feature={t('googleAdsBanner.features.gapAnalysis')} />
                {isGapAnalysisLoading ? (
                  <div
                    data-testid="product-detail-gap-analysis-loading"
                    className="overflow-hidden rounded-2xl border bg-white p-4 shadow-sm"
                  >
                    <Skeleton className="mb-3 h-8 w-full rounded-lg" />
                    <div className="flex flex-col gap-3">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-24 w-full rounded-xl" />
                      ))}
                    </div>
                  </div>
                ) : isGapAnalysisError ? (
                  // Distinct from the empty state below — a failed request must not
                  // read as "this product has no gap data".
                  <div
                    data-testid="product-detail-gap-analysis-error"
                    className="overflow-hidden rounded-2xl border bg-white py-24 text-center shadow-sm"
                  >
                    <p className="text-foreground text-sm leading-5 font-semibold">
                      Gap analysis could not be loaded.
                    </p>
                    <p className="text-muted-foreground text-sm leading-5">
                      Something went wrong fetching this product&apos;s gap analysis. Try reloading.
                    </p>
                  </div>
                ) : (
                  <GapAnalysis gap={gapAnalysis} />
                )}
              </div>
            )}

            {effectiveActiveTab === 'search-descriptors' && detail.status !== 'needs-attention' && (
              <div
                data-testid="product-detail-search-descriptors-tab"
                className="flex flex-col gap-4"
              >
                {/* Shown only when Google Ads isn't connected — competition /
                    search-volume data needs a Google Ads link. */}
                <GoogleAdsConnectBanner feature={t('googleAdsBanner.features.searchDescriptors')} />
                {isSearchDescriptorsLoading ? (
                  <div
                    data-testid="product-detail-search-descriptors-loading"
                    className="overflow-hidden rounded-2xl border bg-white p-5 shadow-sm"
                  >
                    <Skeleton className="mb-4 h-6 w-56 rounded-lg" />
                    <div className="flex flex-col gap-3">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-24 w-full rounded-xl" />
                      ))}
                    </div>
                  </div>
                ) : isSearchDescriptorsError ? (
                  // Distinct from the component's empty states — a failed request must
                  // not read as "this product has no descriptors". The contract's 404
                  // never lands here; the service resolves it to null.
                  <div
                    data-testid="product-detail-search-descriptors-error"
                    className="overflow-hidden rounded-2xl border bg-white py-24 text-center shadow-sm"
                  >
                    <p className="text-foreground text-sm leading-5 font-semibold">
                      Search descriptors could not be loaded.
                    </p>
                    <p className="text-muted-foreground text-sm leading-5">
                      Something went wrong fetching this product&apos;s descriptors. Try reloading.
                    </p>
                  </div>
                ) : (
                  <SearchDescriptors data={searchDescriptors} />
                )}
              </div>
            )}

            {effectiveActiveTab === 'languages' && (
              <LanguageCompareView
                workflowId={enrichment?.workflowId ?? batchDetail?.workflowId ?? ''}
                productId={productId}
              />
            )}

            {effectiveActiveTab === 'version-history' && (
              <div data-testid="product-detail-version-history-tab" className="flex flex-col gap-4">
                <div
                  data-testid="product-detail-version-feed-card"
                  className="overflow-hidden rounded-2xl border bg-white p-5 shadow-sm"
                >
                  <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="text-base font-semibold">{t('versions.feedTitle')}</h2>
                    {/* Count from `totalVersions`, position from `version` — they
                        answer different questions and diverge if history is ever
                        pruned, so an absent count falls back to the position
                        alone rather than restating it as a total. */}
                    {versionEntries !== undefined && (
                      <span
                        data-testid="product-detail-version-feed-summary"
                        className="text-muted-foreground text-sm"
                      >
                        {t('versions.headerSummary', {
                          version: versionEntries.currentVersion,
                          count: versionEntries.totalVersions,
                        })}
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground mb-4 text-sm leading-5">
                    {t('versions.feedSubtitle')}
                  </p>
                  <ProductVersionTimeline
                    testId="product-detail-version-timeline-feed"
                    entries={versionEntries?.entries ?? []}
                    isLoading={versionsLoading}
                    isError={versionsError}
                    selectedVersion={viewingVersion}
                    onSelect={setViewingVersion}
                    variant="feed"
                  />
                </div>

                {/* The curated product's OWN timeline — a different entity with
                    its own counter. A feed-only change (price, availability)
                    versions the feed row above and leaves this untouched, which
                    is why they are separate sections rather than one list. */}
                {detail.internalProductId !== undefined && (
                  <div
                    data-testid="product-detail-version-internal-card"
                    className="overflow-hidden rounded-2xl border bg-white p-5 shadow-sm"
                  >
                    <h2 className="text-base font-semibold">{t('versions.internalTitle')}</h2>
                    <p className="text-muted-foreground mt-1 mb-4 text-sm leading-5">
                      {t('versions.internalSubtitle')}
                    </p>
                    <ProductVersionTimeline
                      testId="product-detail-version-timeline-internal"
                      entries={internalVersionEntries?.entries ?? []}
                      isLoading={internalVersionsLoading}
                      isError={internalVersionsError}
                      // Read-only: selecting an internal version has no detail
                      // view to open, so nothing is selectable here.
                      selectedVersion={null}
                      onSelect={() => undefined}
                      variant="internal"
                    />
                  </div>
                )}

                {/* The payload of the selected archived version, rendered from the
                    same shape the live detail uses. */}
                {viewingVersion !== null && (
                  <div
                    data-testid="product-detail-version-historical-card"
                    className="overflow-hidden rounded-2xl border bg-white p-5 shadow-sm"
                  >
                    <ViewingVersionBanner
                      version={viewingVersion}
                      onReturn={() => {
                        setViewingVersion(null);
                      }}
                    />
                    {historicalLoading ? (
                      <Skeleton
                        data-testid="product-detail-version-historical-loading"
                        className="h-40 w-full rounded-xl"
                      />
                    ) : historicalDetail ? (
                      // A read-only field list rather than the current/optimized
                      // comparison: this is one point in time, not a before-and-
                      // after, and rendering it through a diff component would
                      // imply a comparison that isn't being made.
                      <dl
                        data-testid="product-detail-version-historical-fields"
                        className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2"
                      >
                        {toVersionFieldRows(historicalDetail.fields, tCommon).map(
                          ({ field, value }) => (
                            <div
                              key={field}
                              data-testid={`product-detail-version-historical-field-${field}`}
                              className="flex justify-between gap-4 border-b py-1.5"
                            >
                              <dt className="text-muted-foreground text-sm">{field}</dt>
                              <dd className="text-foreground max-w-[60%] text-right text-sm break-words">
                                {value}
                              </dd>
                            </div>
                          ),
                        )}
                      </dl>
                    ) : (
                      <p
                        data-testid="product-detail-version-historical-unavailable"
                        className="text-muted-foreground text-sm"
                      >
                        {t('versions.historicalUnavailable')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}
